import { updateNodeAndParents } from "common/helpers/EstimateTree";
import { CalculatedFinancingRecord, calculatedFinancingFromJSON } from "common/records/CalculatedFinancing";
import { commissionTestDataFromJSON } from "common/records/CommissionTestData";
import { IDocumentData } from "common/records/Document";
import { EstimateRecord, calcEstimateSubtotals, calcTaxTotals, estimateFromJSON } from "common/records/Estimate";
import { estimateCommissionFromJSON } from "common/records/EstimateCommission";
import { calcDiscount, estimateDiscountFromJSON } from "common/records/EstimateDiscount";
import { IEstimateGroupData, sumEstimateGroupTotals } from "common/records/EstimateGroup";
import {
  EstimateOverviewRecord,
  IEstimateOverviewData,
  fromJSON as estimateOverviewFromJSON,
} from "common/records/EstimateOverview";
import { calcPaymentTerms, estimatePaymentTermsFromJSON } from "common/records/EstimatePaymentTerms";
import { RootActions, RootState } from "common/reducers";
import { clear, fetch, loaded, receive } from "common/reducers/Utils";
import {
  ADD_DISCOUNT,
  ADD_PAYMENT_TERM,
  CONSUME_AUTO_OPEN_LINE_ITEM,
  BASELINE_PAYMENT_TERM_FINANCIALS,
  COPY_ESTIMATE_TO_LAST_SAVED,
  DECREMENT_ESTIMATE_UNSAVED_ID,
  FETCH_ESTIMATE,
  RECEIVE_CALCULATIONS,
  RECEIVE_ESTIMATE,
  RECEIVE_ESTIMATE_DOCUMENTS,
  RECEIVE_ESTIMATE_OVERVIEWS,
  REMOVE_DISCOUNT,
  RESET_DISCOUNTS,
  RESET_ESTIMATE_DOCUMENTS,
  RESET_PAYMENT_TERM,
  SET_AUTO_OPEN_LINE_ITEM,
  SET_CURRENT_ESTIMATE_ID,
  SET_CURRENT_LISTING_TYPE,
  SET_ESTIMATE_BY_ID,
  SET_ESTIMATE_DOCS_LOADED,
  DISCARD_ESTIMATE,
  SET_ESTIMATE_LOADED,
  UPDATE_ESTIMATE_DISCOUNT_TOTALS,
  UPDATE_ESTIMATE_FINANCE_OPTION_PAYMENTS,
  UPDATE_ESTIMATE_METADATA,
  UPDATE_ESTIMATE_PAYMENT_TERMS_TOTALS,
  UPDATE_ESTIMATE_SUBTOTALS,
  UPDATE_ESTIMATE_TAX_TOTALS,
  FETCH_ESTIMATE_COMMISSION,
  RECEIVE_ESTIMATE_COMMISSION,
  REMOVE_ESTIMATE,
} from "common/reducers/estimate.actions";
import { EstimateFinanceOptionActions } from "common/reducers/estimateFinanceOption.actions";
import { reducer as estimateFinanceOptionReducer } from "common/reducers/estimateFinanceOption.reducer";
import { EstimateGroupActions } from "common/reducers/estimateGroup.actions";
import { estimateGroupsReducer } from "common/reducers/estimateGroup.reducer";
import { estimate } from "common/selectors/estimate.selectors";
import { estimateFinanceOptions } from "common/selectors/estimateFinanceOption.selectors";
import { filteredEstimateGroups } from "common/selectors/estimateGroup.selectors";
import { List, Map, Record, fromJS } from "immutable";

export const EstimateStateRecord = Record({
  byId: Map<number, EstimateRecord>(),
  lastSavedById: Map<number, EstimateRecord>(), // What's saved on the SERVER (for dirty tracking)
  editingSnapshotById: Map<number, EstimateRecord>(), // Snapshot for line item rollback
  overviewsById: Map<number, EstimateOverviewRecord>(),
  unsavedId: -1,
  currentEstimateId: null,
  currentListingType: "",
  estimateDocs: Map<number, List<number>>(),
  estimateDocsLoaded: false,
  commissionTestData: commissionTestDataFromJSON({}),
  // Single-slot server-computed commission breakdown for the Commission Calculator (greenline/custom).
  // Not keyed by estimate id — one modal open at a time; each open resets it via FETCH_ESTIMATE_COMMISSION.
  calculatedCommission: estimateCommissionFromJSON({}),
  calculatedFinancingByFinancedAmount: Map<number, Map<number, CalculatedFinancingRecord>>(),
  autoOpenLineItemId: null as number | null,
});

export const initialState = EstimateStateRecord();

export const model = "estimates";

export const reducer = (state: RootState, action: RootActions): RootState => {
  if (state && !state.get(model)) {
    state = state.set(model, initialState);
  }

  switch (action.type) {
    case RECEIVE_ESTIMATE_OVERVIEWS: {
      const { estimates } = action.payload;
      estimates.forEach((estimate: Partial<IEstimateOverviewData>) => {
        state = state.setIn([model, "overviewsById", estimate.id], estimateOverviewFromJSON(estimate));
      });

      return state;
    }
    case SET_CURRENT_ESTIMATE_ID:
      return state.setIn([model, "currentEstimateId"], action.payload.id);
    case SET_AUTO_OPEN_LINE_ITEM:
      return state.setIn([model, "autoOpenLineItemId"], action.payload.lineItemId);
    case CONSUME_AUTO_OPEN_LINE_ITEM:
      return state.setIn([model, "autoOpenLineItemId"], null);
    case SET_CURRENT_LISTING_TYPE:
      return state.setIn([model, "currentListingType"], action.payload.listingType);
    case FETCH_ESTIMATE:
      return fetch(state, model, estimateFromJSON({ id: action.payload.id })) as RootState;
    case DISCARD_ESTIMATE:
      return clear(state, model, action.payload.id);
    case RECEIVE_ESTIMATE: {
      const estimate = action.payload.estimate;

      // Process groups and inject estimate_id since the API doesn't return it
      estimate?.groups?.map((g: IEstimateGroupData) => {
        // Inject estimate_id into group data since API doesn't provide it
        const groupWithEstimateId = { ...g, estimate_id: estimate.id };
        state = estimateGroupsReducer(state, EstimateGroupActions.receiveEstimateGroup(groupWithEstimateId));
      });

      estimate?.finance_options?.forEach((fo) => {
        state = estimateFinanceOptionReducer(state, EstimateFinanceOptionActions.receiveEstimateFinanceOption(fo));
      });

      const estimateRecord = estimateFromJSON(estimate);
      state = receive(state, model, estimateRecord);

      // Copy to lastSavedById - this represents what's saved on the SERVER
      // This is used for dirty tracking to warn users about unsaved changes
      state = state.setIn([model, "lastSavedById", estimate.id], estimateRecord);

      return state;
    }
    case FETCH_ESTIMATE_COMMISSION:
      return state.setIn([model, "calculatedCommission"], estimateCommissionFromJSON({ loading: true }));
    case RECEIVE_ESTIMATE_COMMISSION:
      return state.setIn(
        [model, "calculatedCommission"],
        estimateCommissionFromJSON({ ...action.payload.commissionData, loading: false }),
      );
    case SET_ESTIMATE_LOADED:
      return loaded(state, model, action.payload.id) as RootState;
    case SET_ESTIMATE_DOCS_LOADED:
      return state.setIn([model, "estimateDocsLoaded"], action.payload) as RootState;
    case RESET_ESTIMATE_DOCUMENTS:
      return state.setIn([model, "estimateDocs"], Map<number, List<number>>()) as RootState;

    case RECEIVE_ESTIMATE_DOCUMENTS: {
      const { estimateDocs } = action.payload;

      estimateDocs.forEach((doc: IDocumentData) => {
        state = state.updateIn([model, "estimateDocs", doc?.signed_document?.estimate_id], (list: List<any>) => {
          const updatedList = list || List();
          return updatedList.includes(doc.id) ? updatedList : updatedList.push(doc.id);
        });
      });

      return state;
    }

    case UPDATE_ESTIMATE_SUBTOTALS: {
      const { estimateId } = action.payload;
      const estimateRecord = estimate(state, { estimateId });

      if (!estimateRecord) {
        console.error("Missing required data in redux to calculate estimate subtotals", {
          estimateId,
          estimate: estimateRecord,
        });
        return state;
      }

      const estimateGroupRecords = filteredEstimateGroups(state, { estimateGroupIds: estimateRecord.group_ids });
      const groupTotals = sumEstimateGroupTotals(estimateGroupRecords);
      const subtotals = calcEstimateSubtotals(groupTotals);
      return state.setIn([model, "byId", estimateId], estimateRecord.merge(subtotals));
    }

    case UPDATE_ESTIMATE_DISCOUNT_TOTALS: {
      const { estimateId } = action.payload;
      const estimateRecord = estimate(state, { estimateId });

      if (!estimateRecord) {
        console.error("Missing required data in redux to calculate estimate discount totals", {
          estimateId,
          estimate: estimateRecord,
        });
        return state;
      }

      const discountTotals = calcDiscount(estimateRecord);
      return state.setIn([model, "byId", estimateId], estimateRecord.merge(discountTotals));
    }

    case UPDATE_ESTIMATE_TAX_TOTALS: {
      const { estimateId } = action.payload;
      const estimateRecord = estimate(state, { estimateId });

      if (!estimateRecord) {
        console.error("Missing required data in redux to calculate estimate tax totals", {
          estimateId,
          estimate: estimateRecord,
        });
        return state;
      }

      const taxTotals = calcTaxTotals(estimateRecord);
      return state.setIn([model, "byId", estimateId], estimateRecord.merge(taxTotals));
    }

    case UPDATE_ESTIMATE_PAYMENT_TERMS_TOTALS: {
      const { estimateId } = action.payload;
      const estimateRecord = estimate(state, { estimateId });

      if (!estimateRecord) {
        console.error("Missing required data in redux to calculate estimate payment terms totals", {
          estimateId,
          estimate: estimateRecord,
        });
        return state;
      }

      const paymentTermsTotals = calcPaymentTerms(estimateRecord.payment_terms, estimateRecord.total);
      return state.setIn([model, "byId", estimateId], estimateRecord.merge(paymentTermsTotals));
    }

    case UPDATE_ESTIMATE_FINANCE_OPTION_PAYMENTS: {
      const { estimateId } = action.payload;
      const estimateRecord = estimate(state, { estimateId });

      if (!estimateRecord) {
        console.error("Missing required data in redux to calculate estimate finance option payments", {
          estimateId,
          estimate: estimateRecord,
        });
        return state;
      }

      // Get all estimate finance options for this estimate
      const financeOptionIds = estimateRecord.finance_option_ids;
      if (!financeOptionIds || financeOptionIds.size === 0) {
        return state;
      }

      // Get the estimate finance options that exist in the store
      const efoList = estimateFinanceOptions(state, { estimateFinanceOptionIds: financeOptionIds });

      // Calculate payments for each estimate finance option
      efoList.forEach((efo) => {
        if (efo && !efo._destroy) {
          state = estimateFinanceOptionReducer(
            state,
            EstimateFinanceOptionActions.calculateEstimateFinanceOption(estimateId, efo.id),
          );
        }
      });

      return state;
    }

    case DECREMENT_ESTIMATE_UNSAVED_ID: {
      const currentUnsavedId = state.getIn([model, "unsavedId"]);
      return state.setIn([model, "unsavedId"], currentUnsavedId - 1);
    }

    case SET_ESTIMATE_BY_ID: {
      const { estimateId, estimate: estimateData } = action.payload;
      const estimateRecord = estimateFromJSON(estimateData);
      return state.setIn([model, "byId", estimateId], estimateRecord);
    }

    case UPDATE_ESTIMATE_METADATA: {
      const { estimateId, metadataUpdate } = action.payload;
      const existingMetadata = state.getIn([model, "byId", estimateId, "metadata"]) as Map<string, unknown>;
      const updatedMetadata = existingMetadata.merge(fromJS(metadataUpdate));
      return state.setIn([model, "byId", estimateId, "metadata"], updatedMetadata);
    }

    case COPY_ESTIMATE_TO_LAST_SAVED: {
      const { estimateId } = action.payload;
      const estimateRecord = state.getIn([model, "byId", estimateId]) as EstimateRecord | undefined;
      if (!estimateRecord) {
        return state;
      }
      return state.setIn([model, "lastSavedById", estimateId], estimateRecord);
    }

    case BASELINE_PAYMENT_TERM_FINANCIALS: {
      // Only these two keys re-baseline onto the server values; all other fields stay on the client recompute.
      const { estimateId, financedAmount, downPayment } = action.payload;
      const saved = state.getIn([model, "lastSavedById", estimateId]) as EstimateRecord | undefined;
      if (!saved) {
        return state;
      }
      return state
        .setIn([model, "lastSavedById", estimateId, "payment_terms", "financed_amount"], financedAmount)
        .setIn([model, "lastSavedById", estimateId, "payment_terms", "down_payment"], downPayment);
    }

    case RECEIVE_CALCULATIONS: {
      const { calculations, financedAmount } = action.payload;

      calculations.forEach((calculation) => {
        const path = [model, "calculatedFinancingByFinancedAmount", financedAmount, calculation.finance_option_id];
        state = state.setIn(path, calculatedFinancingFromJSON({ ...calculation, loading: false }));
      });

      return state;
    }

    case ADD_DISCOUNT: {
      const { discountData, estimateId } = action.payload;

      const estimateRecord = estimate(state, { estimateId });
      if (!estimateRecord) {
        console.error("Cannot add discount: estimate not found", { estimateId });
        return state;
      }

      const estimateDiscount = estimateDiscountFromJSON(discountData);
      const updatedDiscounts = estimateRecord.discounts.push(estimateDiscount);
      state = state.setIn(["estimates", "byId", estimateId, "discounts"], updatedDiscounts);

      return updateNodeAndParents(state, "discountTotals", estimateId);
    }

    case ADD_PAYMENT_TERM: {
      const { estimateId, template } = action.payload;

      const newPaymentTerms = estimatePaymentTermsFromJSON({
        description: template.description,
        id: template.id,
        name: template.name,
        payment_term_items: template.breakdown.items.toJS(),
      });

      state = state.setIn([model, "byId", estimateId, "payment_terms"], newPaymentTerms);

      return updateNodeAndParents(state, "paymentTermsTotals", estimateId);
    }

    case REMOVE_DISCOUNT: {
      const estimateId =
        typeof action.payload.estimateId === "string" ? parseInt(action.payload.estimateId) : action.payload.estimateId;
      const uuid =
        typeof action.payload.discount === "string" ? action.payload.discount : action.payload.discount.get("uuid");

      const estimate = state
        .getIn([model, "byId"])
        .get(estimateId)
        ?.update("discounts", (discounts) => discounts.filter((d) => d.uuid !== uuid));

      state = state.setIn([model, "byId", estimateId], estimate);

      return updateNodeAndParents(state, "discountTotals", estimateId);
    }

    case RESET_DISCOUNTS: {
      const { estimateId } = action.payload;

      const estimateRecord = state.getIn([model, "byId"]).get(estimateId);
      if (!estimateRecord) {
        return state;
      }

      state = state.setIn([model, "byId", estimateId], estimateRecord.set("discounts", List()));

      return updateNodeAndParents(state, "discountTotals", estimateId);
    }

    case RESET_PAYMENT_TERM: {
      const { estimateId } = action.payload;
      if (!estimateId) {
        return state;
      }

      state = state.setIn([model, "byId", estimateId, "payment_terms"], estimatePaymentTermsFromJSON({}));

      return updateNodeAndParents(state, "paymentTermsTotals", estimateId);
    }

    case REMOVE_ESTIMATE: {
      const { estimateId } = action.payload;
      if (!estimateId) {
        return state;
      }

      state = state.removeIn([model, "byId", estimateId]);
      state = state.removeIn([model, "lastSavedById", estimateId]);

      if (estimateId === state.getIn([model, "currentEstimateId"])) {
        state = state.setIn([model, "currentEstimateId"], null);
      }

      return state;
    }

    default:
      return state;
  }
};
